import { useEffect, useState } from 'react';

export interface DisconnectBannerProps {
  /** true when OUR OWN socket is not currently open. */
  selfDisconnected: boolean;
  /** true when the partner seat shows connected:false server-side. */
  partnerDisconnected: boolean;
}

/** TOP-10 #8 fix: previously a plain full-width `.banner` left-aligned
 * inside `.stage-top` (which is wider than the banner's own max-width, so
 * with no `align-self`/margin it rendered flush against the top-left
 * corner -- reads as a stray debug card, not a deliberate system message),
 * with no icon and no way to dismiss it. Centered (`.banner`'s own
 * `align-self: center` below), an explicit warning icon, and a real
 * dismiss control -- same "system toast" language as ErrorBanner. Dismiss
 * is LOCAL UI state only (never suppresses the underlying disconnect;
 * SPEC.md §1.1 -- the banner reappears immediately if a NEW disconnect
 * edge occurs, e.g. self reconnects then drops again, or the partner
 * reconnects then disconnects again). */
export function DisconnectBanner({ selfDisconnected, partnerDisconnected }: DisconnectBannerProps): JSX.Element | null {
  const showing = selfDisconnected || partnerDisconnected;
  const [dismissed, setDismissed] = useState(false);

  // Reset the local dismiss whenever the underlying condition flips from
  // "not showing" to "showing" again -- a fresh disconnect is a fresh
  // notification, not a resurrection of the one the player already closed.
  useEffect(() => {
    if (!showing) setDismissed(false);
  }, [showing]);

  if (!showing || dismissed) return null;

  return (
    <div className="banner banner-warn" role="alert" data-testid="disconnect-banner">
      <span className="banner-icon" aria-hidden="true">
        ⚠
      </span>
      <span className="banner-message">
        {selfDisconnected
          ? 'Reconnecting to the server...'
          : "Your partner disconnected. They can rejoin any time; their Pac-Man is frozen, not lost."}
      </span>
      <button
        type="button"
        className="banner-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        data-testid="disconnect-banner-dismiss"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  );
}
